import { useQuery } from "@tanstack/react-query";
import { fetchPublicBranches, type PublicBranch } from "./api";

export type { PublicBranch };

/**
 * Static branch list, used only when the public endpoint cannot be reached.
 *
 * `regionLabel` replaces the old `city` field: the region name now comes from
 * the catalog's root group, so a director can add a region without a rebuild.
 */
export const PUBLIC_BOOKING_BRANCHES: PublicBranch[] = [
  {
    code: "sg_32",
    label: "Gạo Nâu SG - Đường 3/2",
    shortLabel: "SG · Đường 3/2",
    address: "Chi nhánh Đường 3/2, TP.HCM",
    regionLabel: "TP.HCM",
  },
  {
    code: "sg_lvs",
    label: "Gạo Nâu SG - Lê Văn Sỹ",
    shortLabel: "SG · Lê Văn Sỹ",
    address: "Chi nhánh Lê Văn Sỹ, TP.HCM",
    regionLabel: "TP.HCM",
  },
  {
    code: "hn_dl",
    label: "Gạo Nâu HN - Nguyễn Khang",
    shortLabel: "HN · Nguyễn Khang",
    address: "Chi nhánh Nguyễn Khang, Hà Nội",
    regionLabel: "Hà Nội",
  },
];

/**
 * Thông tin trưng bày cho từng cơ sở — ảnh vuông + tên + địa chỉ thật,
 * lấy từ trang Liên hệ của web gaonauprofile.com (CEO chốt 20/08/2026).
 *
 * Đây là lớp phủ THUẦN HIỂN THỊ đè lên dữ liệu catalog: cơ sở nào chưa có
 * trong map (mở thêm sau này) tự rơi về tên/địa chỉ từ API + icon mặc định,
 * nên thêm chi nhánh mới không cần sửa FE.
 */
export type BrandedBranch = PublicBranch & { photo?: string };

const BRANCH_DISPLAY: Record<string, Partial<PublicBranch> & { photo?: string }> = {
  sg_32: {
    shortLabel: "HCM Q.10 · Đường 3/2",
    address: "Số 436A/71, Đường 3/2, Phường Hòa Hưng, TP Hồ Chí Minh",
    photo: "/images/branches/sg_32.jpg",
  },
  // "Lê Văn Sỹ 1" chứ không phải "Lê Văn Sỹ": từ 02/09/2026 có hai cơ sở cùng
  // một con đường (351/45 và 351/43). Bỏ chữ số ở đây là khách đứng trước hai
  // thẻ giống hệt nhau và chọn bừa — nhãn này là chỗ DUY NHẤT phân biệt được.
  sg_lvs: {
    shortLabel: "HCM Q.3 · Lê Văn Sỹ 1",
    address: "351/45 Lê Văn Sỹ, Phường Nhiêu Lộc, Thành Phố Hồ Chí Minh",
    photo: "/images/branches/sg_lvs.jpg",
  },
  hn_dl: {
    shortLabel: "HN · Nguyễn Khang",
    address: "Tầng 4, 83 Nguyễn Khang, Phường Yên Hòa, Hà Nội",
    photo: "/images/branches/hn_dl.jpg",
  },
  // Hai cơ sở mở 02/09/2026. CHƯA có ảnh thật (`photo` bỏ trống → icon mặc
  // định); nhãn vẫn phải phủ, nếu không khách đọc đúng chữ trong máy —
  // "LÊ VĂN SỸ 2" in hoa, không có tên thành phố, đứng cạnh các thẻ viết hoa
  // thường đàng hoàng.
  sg_lvs2: {
    shortLabel: "HCM Q.3 · Lê Văn Sỹ 2",
    address: "351/43 Lê Văn Sỹ, Phường Nhiêu Lộc, Thành Phố Hồ Chí Minh",
  },
  hn_tc: {
    shortLabel: "HN · Trường Chinh",
    address: "Meco Complex, Ngõ 102 Trường Chinh, Hà Nội",
  },
};

export function brandBranch(branch: PublicBranch): BrandedBranch {
  const display = BRANCH_DISPLAY[branch.code];
  return display ? { ...branch, ...display } : branch;
}

export function publicBranchByCode(
  code: string,
  branches: readonly PublicBranch[] = PUBLIC_BOOKING_BRANCHES,
): BrandedBranch | null {
  const found = branches.find((branch) => branch.code === code);
  return found ? brandBranch(found) : null;
}

/**
 * Publicly bookable branches from the API, falling back to the static list.
 *
 * A booking page with no branches would be unusable, so a failed fetch shows
 * the last known list rather than nothing.
 */
export function usePublicBranches() {
  const query = useQuery({
    queryKey: ["public-branches"],
    queryFn: ({ signal }) => fetchPublicBranches({ signal }),
    staleTime: 5 * 60_000,
  });
  return {
    branches: (query.data ?? PUBLIC_BOOKING_BRANCHES).map(brandBranch),
    isFallback: !query.data && !query.isLoading,
  };
}

